import React, { FunctionComponent, MutableRefObject, ReactNode } from 'react';

import styled from 'styled-components';

import Box from '@material-ui/core/Box';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';

import Quote from '../Quote';

const BackgroundContainer = styled(Box)`
  ${({ theme: { palette } }) => `
    background: url('./assets/pattern-x3.png'), linear-gradient(45deg, ${palette.secondary.main}, ${palette.secondary.main} 50%, ${palette.primary.main});
    background-attachment: fixed;
    box-shadow: inset 1px 1px 10px rgba(0,0,0,0.2);
  `}
`;

type StyledContainer = {
  ref?: MutableRefObject<null>;
  maxWidth?: boolean;
  size?: 40 | 80 | 144;
};

const StyledContainer = styled(Box)<StyledContainer>`
  padding: ${({ size }) => size || 144}px 0;

  @media screen and (max-width: 700px) {
    padding: ${({ size }) => size || 80}px 0;
  }
`;

type Content = StyledContainer & {
  sectionRef?: MutableRefObject<null>;
  supportContent?: ReactNode;
};

type Section = Content & {
  alignment: 'left' | 'center' | 'right';
};

type QuoteSection = {
  href: string;
  subtitle: string;
  title: string;
};

export const QuoteSection: FunctionComponent<QuoteSection> = ({
  href,
  subtitle,
  title,
}) => {
  return (
    <BackgroundContainer>
      <Container maxWidth="lg">
        <StyledContainer size={80}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}></Grid>
            <Grid item xs={12} md={6}>
              <Quote href={href} subtitle={subtitle}>
                {title}
              </Quote>
            </Grid>
            <Grid item xs={12} md={3}></Grid>
          </Grid>
        </StyledContainer>
      </Container>
    </BackgroundContainer>
  );
};

export const ContentSection: FunctionComponent<Section> = ({
  alignment,
  children,
  sectionRef,
  size,
  supportContent,
}) => {
  switch (alignment) {
    case 'left':
      return (
        <ContentLeft
          sectionRef={sectionRef}
          supportContent={supportContent}
          size={size}
        >
          {children}
        </ContentLeft>
      );
    case 'center':
      return (
        <ContentCenter
          sectionRef={sectionRef}
          supportContent={supportContent}
          size={size}
        >
          {children}
        </ContentCenter>
      );
    case 'right':
      return (
        <ContentRight
          sectionRef={sectionRef}
          supportContent={supportContent}
          size={size}
        >
          {children}
        </ContentRight>
      );
  }
};

const ContentLeft: FunctionComponent<Content> = ({
  children,
  sectionRef,
  size,
  supportContent,
}) => {
  return (
    <Container maxWidth="lg">
      <StyledContainer maxWidth="lg" ref={sectionRef} size={size}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={6}>
            {children}
          </Grid>
          <Grid item xs={12} md={6}>
            {supportContent}
          </Grid>
        </Grid>
      </StyledContainer>
    </Container>
  );
};

const ContentRight: FunctionComponent<Content> = ({
  children,
  sectionRef,
  size,
  supportContent,
}) => {
  return (
    <Container maxWidth="lg">
      <StyledContainer maxWidth="lg" ref={sectionRef} size={size}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={6}>
            {supportContent}
          </Grid>
          <Grid item xs={12} md={6}>
            {children}
          </Grid>
        </Grid>
      </StyledContainer>
    </Container>
  );
};

const ContentCenter: FunctionComponent<Content> = ({
  children,
  sectionRef,
  size,
}) => {
  return (
    <Container maxWidth="lg">
      <StyledContainer maxWidth="lg" ref={sectionRef} size={size}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={3}></Grid>
          <Grid item xs={12} md={6}>
            {children}
          </Grid>
          <Grid item xs={12} md={3}></Grid>
        </Grid>
      </StyledContainer>
    </Container>
  );
};
